import { useEffect, useRef, useState } from 'react';

import { device } from 'commons';
import {
  Anchor,
  Captcha,
  DashboardConsignmentList,
  DashboardFilter,
  DrawerProvider,
  Loading,
  Main,
  MaterialDrawer,
} from 'components';
import { useAuth, useConsignmentsDetails } from 'hooks';
import { Consignment, PosType } from 'types';
import { StoreDetailsStore } from 'zustand-store';

import {
  BaseHomePage,
  ConsignmentDetails,
  NotificationStripe,
  SuperExpressModal,
  WarningBox,
} from './components';
import * as S from './styles';

export function DashboardPage() {
  const { selectedStore } = useAuth();
  const { isStoreOpen, isStoreActive, storeDetails } =
    StoreDetailsStore.useGetStoreDetails();
  const { posType } = storeDetails;
  const { innerWidth } = window;

  const [superExpressModal, setSuperExpressModal] = useState(false);
  const [superExpressConsignments, setSuperExpressConsignments] = useState<
    Consignment[]
  >([]);
  const [isBot, setIsBot] = useState(false);
  const [selectedConsignment, setSelectedConsignment] =
    useState<Consignment | null>(null);

  const firstRender = useRef(true);

  useEffect(() => {
    setSelectedConsignment(null);

    if (firstRender.current) {
      firstRender.current = false;

      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  const { consignmentCode } = selectedConsignment || {};

  const {
    data: consignmentDetailsData,
    isValidating,
    error,
  } = useConsignmentsDetails(consignmentCode);

  const isMobile = innerWidth <= device.mobile;

  function renderWarningBox() {
    if (isStoreActive && isStoreOpen) return null;

    const isFranchise = posType === PosType.Franchise;

    return (
      <>
        <div className="mx-8 flex flex-col gap-2">
          <WarningBox
            template={!isStoreActive ? 'inactiveStore' : 'closedStore'}
          />

          {!isStoreActive && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-neutral-700">
                {isFranchise ? (
                  <>
                    Estamos aqui para ajudar você a retomar sua operação.{' '}
                    <b className="font-bold">
                      Envie um plano de ação para reativar sua loja no Retire &
                      Entrega.
                    </b>
                  </>
                ) : (
                  <b className="font-bold">
                    Para reativar sua operação, entre em contato com o time
                    comercial.
                  </b>
                )}
              </p>

              {isFranchise && (
                <Anchor
                  href="https://docs.google.com/forms/d/e/1FAIpQLSds_z2oDrfU_X9h4PXFBbsJitKRi8kE1dAO6WjW2nCluobNjw/viewform"
                  variant="primary"
                  target="_blank"
                  full
                >
                  Criar plano de ação
                </Anchor>
              )}
            </div>
          )}
        </div>

        {!isStoreActive && <div className="h-px w-full bg-neutral-300" />}
      </>
    );
  }

  return (
    <Main>
      <DrawerProvider>
        {!isStoreActive && <NotificationStripe />}

        {superExpressModal && (
          <SuperExpressModal
            handleClose={() => setSuperExpressModal(false)}
            consignments={superExpressConsignments}
            setSelectedConsignment={setSelectedConsignment}
          />
        )}

        <S.Wrapper>
          {isBot && <Captcha setIsBot={setIsBot} />}

          {isMobile ? (
            <>
              <MaterialDrawer position="right">
                <S.DetailsSection>
                  {selectedConsignment === null ? (
                    <BaseHomePage />
                  ) : consignmentDetailsData ? (
                    <>
                      {isValidating && (
                        <S.CentralizedText>
                          <Loading />
                        </S.CentralizedText>
                      )}

                      <ConsignmentDetails
                        key={consignmentDetailsData.code}
                        setSelectedConsignment={setSelectedConsignment}
                        consignmentData={consignmentDetailsData}
                        isError={error}
                      />
                    </>
                  ) : (
                    <S.CentralizedText>
                      <Loading />
                    </S.CentralizedText>
                  )}
                </S.DetailsSection>
              </MaterialDrawer>

              <S.ConsignmentsSection>
                {renderWarningBox()}

                <DashboardFilter />

                <DashboardConsignmentList
                  setSelectedConsignment={setSelectedConsignment}
                  setIsBot={setIsBot}
                  selectedConsignment={selectedConsignment}
                  setSuperExpressModal={setSuperExpressModal}
                  setSuperExpressConsignments={setSuperExpressConsignments}
                />
              </S.ConsignmentsSection>
            </>
          ) : (
            <>
              <S.ConsignmentsSection>
                {renderWarningBox()}

                <DashboardFilter />

                <DashboardConsignmentList
                  setSelectedConsignment={setSelectedConsignment}
                  setIsBot={setIsBot}
                  selectedConsignment={selectedConsignment}
                  setSuperExpressModal={setSuperExpressModal}
                  setSuperExpressConsignments={setSuperExpressConsignments}
                />
              </S.ConsignmentsSection>

              <S.DetailsSection>
                {selectedConsignment === null ? (
                  <BaseHomePage />
                ) : consignmentDetailsData ? (
                  <>
                    {isValidating && (
                      <S.CentralizedText>
                        <Loading />
                      </S.CentralizedText>
                    )}

                    <ConsignmentDetails
                      key={consignmentDetailsData.code}
                      setSelectedConsignment={setSelectedConsignment}
                      consignmentData={consignmentDetailsData}
                      isError={error}
                    />
                  </>
                ) : (
                  <S.CentralizedText>
                    <Loading />
                  </S.CentralizedText>
                )}
              </S.DetailsSection>
            </>
          )}
        </S.Wrapper>
      </DrawerProvider>
    </Main>
  );
}
